/**
 * Channel History API Client
 *
 * Functions for channel-level version history and rollback.
 */
import { apiClient } from '@/lib/api-client';

export interface ChannelVersionSlot {
  link_id: string;
  slot: number;
  asset_id: string;
  s3_key: string;
  s3_url: string | null;
  width: number;
  height: number;
  alt_text: string | null;
}

export interface ChannelVersion {
  version_id: string;
  version_number: number;
  created_at: string;
  changed_by: string | null;
  source_type: 'user_edit' | 'rollback' | 'manual_publish' | 'regenerate';
  status: 'draft' | 'published' | 'archived';
  slots: ChannelVersionSlot[];
}

export interface ChannelHistoryResponse {
  success: boolean;
  versions: ChannelVersion[];
}

export interface RollbackResponse {
  success: boolean;
  updated_slots?: number;
  new_version_id?: string;
  message: string;
  error?: string;
}

export interface ChannelCurrentVersion {
  version_number: number;
  created_at: string;
  changed_by: string | null;
  source_type: 'user_edit' | 'rollback' | 'manual_publish' | 'regenerate';
  total_slots: number;
}

export interface ChannelCurrentVersionResponse {
  success: boolean;
  current_version: ChannelCurrentVersion | null;
}

/**
 * Get current version info for a channel (lightweight)
 */
export async function getChannelCurrentVersion(
  productId: string,
  channelId: string
): Promise<ChannelCurrentVersionResponse> {
  const response = await apiClient.get<ChannelCurrentVersionResponse>(
    `/api/v1/products/${productId}/channels/${channelId}/current-version`
  );
  return response.data;
}

/**
 * Get complete version history for a channel
 */
export async function getChannelHistory(
  productId: string,
  channelId: string,
  limit: number = 50
): Promise<ChannelHistoryResponse> {
  const response = await apiClient.get<ChannelHistoryResponse>(
    `/api/v1/products/${productId}/channels/${channelId}/history?limit=${limit}`
  );
  return response.data;
}

/**
 * Rollback complete channel to a specific version
 */
export async function rollbackChannelVersion(
  productId: string,
  channelId: string,
  versionId: string
): Promise<RollbackResponse> {
  const response = await apiClient.post<RollbackResponse>(
    `/api/v1/products/${productId}/channels/${channelId}/rollback`,
    { version_id: versionId }
  );
  return response.data;
}

/**
 * Rollback a single slot to a specific version
 */
export async function rollbackChannelSlot(
  productId: string,
  channelId: string,
  versionId: string,
  slot: number
): Promise<RollbackResponse> {
  const response = await apiClient.post<RollbackResponse>(
    `/api/v1/products/${productId}/channels/${channelId}/rollback-slot`,
    { version_id: versionId, slot }
  );
  return response.data;
}
